import { useId, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import {
  BUTTON_CLASS,
  compareSortValues,
  ConsentDialog,
  Eyebrow,
  formatUtcDate,
  neutralButtonStyle,
  primaryButtonStyle,
  StatusDot,
  token,
} from '@bp-files/ui';
import { FileTable } from '@/components/FolderView/FileRows';
import { useFileDownload } from '@/components/FolderView/useFileDownload';
import {
  CheckIcon,
  FilterIcon,
  MoreActionsIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from '@/components/FolderView/FolderIcons';
import { FOLDER_COPY } from '@/components/FolderView/folderView.copy';
import { ImageViewer } from '@/components/ImageViewer/ImageViewer';
import { PdfViewer } from '@/components/PdfViewer/PdfViewer';
import { isViewableImage, isViewablePdf } from '@/components/Viewer/viewable';
import { UploadPanel } from '@/components/UploadPanel/UploadPanel';
import { UPLOAD_COPY } from '@/components/UploadPanel/uploadPanel.copy';
import type { DropMode } from '@/components/UploadPanel/UploadDropTarget';
import { formatBytes } from '@/lib/formatBytes';
import { ApiError, messageFrom } from '@bp-files/client';
import { useFiles } from '@/lib/useFiles';
import { useFolders } from '@/lib/useFolders';
import { useFolderSubscription } from '@/lib/useFolderSubscription';
import { folderDestinationsChanged, useFolderDestinations } from '@/lib/useFolderDestinations';
import { useViewMode } from '@/lib/useViewMode';
import {
  createFolder,
  deleteFile,
  deleteFolder,
  renameFolder,
  retentionClassSchema,
  setFileRetention,
  setFolderRetention,
  type FileSummary,
  type FolderRetentionResult,
  type Me,
  type RetentionClass,
} from '@/lib/api';

/**
 * The folder view — the file table and the way into it (SPEC §6, FIL-4..8).
 *
 * The last step of the navigation: space list → space root → **folder**. There
 * is nothing below it; FIL-2 fixes the structure at two levels.
 *
 * Both ids come from the URL, because the space root is an address (ADR-0007)
 * and a folder under it is reached by navigation rather than by state.
 *
 * **Three rules this encodes rather than decorates:**
 *
 * - The rows are rendered in the order the server sent. It owns "newest first"
 *   (FIL-4), and a client-side re-sort of one page would be a half-sorted table.
 * - A download URL is never held in the markup. It is minutes-lived and must
 *   never be stored (SEC-3), so the button asks for one when it is pressed.
 * - **The space is fetched supplementally, and never gates the table.** The
 *   files response carries no folder name and no `canWrite`, so the space
 *   supplies the header and decides whether upload is offered (FIL-7, CON-13).
 *   If that request fails the table still renders and the panel is simply
 *   absent — a screen that already worked must not start failing because a
 *   decorative second request did.
 */

/**
 * The folder's shape, in the order the product asked for it by name: which
 * agency, when it last moved, how many files, how much they weigh.
 *
 * `·` between segments rather than a delimiter-joined string built in copy —
 * `addedBy` in `folderView.copy.ts` takes that shape because both its halves
 * are plain text, but this line's date segment is not: it needs the
 * amber-for-time treatment, so it needs to be its own element, and a string
 * cannot carry one.
 */
function FolderSummaryLine({
  spaceName,
  lastActivityAt,
  fileCount,
  totalBytes,
}: {
  spaceName?: string;
  lastActivityAt: string;
  fileCount: number;
  totalBytes: number;
}) {
  return (
    <p
      className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs"
      style={{ color: token('bp-text-secondary') }}
    >
      {spaceName === undefined ? null : (
        <>
          <span>{spaceName}</span>
          <span>·</span>
        </>
      )}
      <time
        dateTime={lastActivityAt}
        style={{ color: token('bp-amber'), fontVariantNumeric: 'tabular-nums' }}
      >
        {FOLDER_COPY.summary.lastActivity(formatUtcDate(lastActivityAt, FOLDER_COPY.dateLocale))}
      </time>
      <span>·</span>
      <span>{FOLDER_COPY.summary.files(fileCount)}</span>
      <span>·</span>
      <span>{formatBytes(totalBytes, FOLDER_COPY.units)}</span>
    </p>
  );
}

export interface FolderViewProps {
  /**
   * `me.agency.plan`, for RET-5's Evidence gate. A prop rather than a second
   * `useMe()`, on `MembersTab`'s `agencyDomain` precedent: that hook has one
   * call site by design, and a second would be a second session state machine
   * that can disagree with the first.
   */
  plan: Me['agency']['plan'];
  /**
   * `me.agency.id` — which row on the space's roster is *us*, and therefore
   * which `visibilityScope` applies to this reader (CON-24).
   *
   * A prop for the same reason `plan` is, and it buys one thing: the empty
   * state. The API applies the scope where the file is read (ADR-0068), so a
   * client receives a table that is already narrowed and has nothing to filter —
   * what it cannot know without this is *why* the table it received is short.
   * `SpaceList` already takes the same value from the same place.
   */
  viewerAgencyId: string;
  /**
   * Gates New subfolder (FIL-3), alongside `canWrite`. A prop rather than a
   * second session read, on `plan`'s own precedent above — and the reason this
   * component needs it at all now: creation moved here from `FolderDestinations`,
   * whose route already threads it for a different reason (`EmptyDestinations`'
   * admin-only empty state).
   */
  isAdmin: boolean;
}

export function FolderView({ plan, viewerAgencyId, isAdmin }: FolderViewProps) {
  const { spaceId, folderId } = useParams();
  const { state, retry, refresh } = useFiles(spaceId ?? '', folderId ?? '');
  const space = useFolders(spaceId ?? '');
  const destinations = useFolderDestinations();
  /**
   * FIL-8a's open image, as `?view={fileId}` (ADR-0007, ADR-0053).
   *
   * **A search parameter and not a path segment.** FIL-2 fixes the tree at
   * exactly two levels and this component's own header says nothing nests below
   * it, so a third segment would read as a third level of structure; "a state
   * of this folder view" is what the viewer actually is. It buys Back-to-close
   * and a space a reviewer can send a colleague — the API still authorizes per
   * request (SEC-5), so the address discloses nothing — and it leaves this
   * component mounted, so opening and closing never re-runs `useFiles`'
   * exhaustive walk.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  /** Whether *this* screen pushed the history entry, and may therefore pop it. */
  const openedHere = useRef(false);
  const viewMode = useViewMode();
  // **The API's own sentence, not a fixed line.** It used to be a boolean, on
  // the grounds that a failed download "has one line and always the same one".
  // The caps slice made that false: PLN-11 requires a throttled download name
  // the cap and whom to ask, and a reader told only "that file could not be
  // downloaded" will click again — which is the retry the cap exists to
  // prevent. `messageFrom` keeps the generic line as the fallback for every
  // other failure.
  const [downloadError, setDownloadError] = useState<string | undefined>(undefined);
  // Separate from the above, because a refused delete and a refused download
  // are different sentences about different acts, and one alert replacing the
  // other would tell the reader the wrong thing happened.
  const [actionError, setActionError] = useState<string | undefined>(undefined);
  // And separate again, because this one is not an error at all: a started
  // retrieval succeeded, and it is the only thing on this screen that reports a
  // success in prose (RET-9).
  const [rehydrationNotice, setRehydrationNotice] = useState<string | undefined>(undefined);
  // **One row confirms a delete at a time.** This was widened to a `mode` when
  // retention shared the same decision step; retention now lives in the shared
  // overflow as a direct choice with no confirm step of its own (setting a class
  // takes nothing away), so the only thing left to confirm is delete and a
  // bare id says that on its own.
  const [confirming, setConfirming] = useState<string | undefined>(undefined);
  const [busyId, setBusyId] = useState<string | undefined>(undefined);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkReport, setBulkReport] = useState<FolderRetentionResult | undefined>(undefined);
  // Shared with `UploadPanel`'s file input via `<label htmlFor>` — no DOM
  // proximity needed, only a matching id — so the header trigger below opens
  // the OS picker directly. Not state: there is nothing to toggle.
  const uploadInputId = useId();
  // The header's own overflow — the switch and the bulk retention picker,
  // behind one trigger on `RowActions`' precedent. `absolute`, not a portal:
  // that component's `fixed` positioning exists to escape `DataTable`'s
  // `overflow-x-auto`, and this trigger sits in the header, outside it.
  const [controlsOpen, setControlsOpen] = useState(false);
  const controlsBlurTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  /*
    **Whether the press that is blurring the trigger is landing inside the
    panel**, and it is what makes the panel survive a click that takes longer
    than 120ms.

    The deferred blur alone does not: the order is mousedown → blur → mouseup →
    click, so the panel's `onMouseDown` clears a timer that the *blur* then
    re-arms, and any press held longer than the delay closes the panel before
    its own click can land. Clearing the timer inside each item's `onClick` is
    too late for the same reason. A deliberate press is easily 150ms, so the
    control simply did nothing — intermittently, and faster on a fast machine.

    Set on the panel's mousedown and cleared on mouseup, so the trigger's blur
    can tell "focus left because the reader is done" from "focus left because
    the reader is pressing something in here".
  */
  const controlsPointerDown = useRef(false);

  // CON-24's filter — its own trigger and its own popover, not a fourth group
  // behind "Folder settings": that panel holds folder facts and standing
  // preferences, and a filter changes what the table shows instead. Same
  // deferred-blur-close mechanism as `controlsOpen` above, in its own
  // instances, so opening one panel never races the other's timer.
  const [filterOpen, setFilterOpen] = useState(false);
  const filterBlurTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const filterPointerDown = useRef(false);
  // The selected agency's display name, or `undefined` for "All agencies".
  // Keyed on the name and not an id: a file's `uploadedBy` carries no
  // `agencyId` (`fileSummarySchema`), only `agencyName`, so the filter has to
  // match what the rows actually have.
  const [agencyFilter, setAgencyFilter] = useState<string | undefined>(undefined);
  // `FolderView` does not remount folder-to-folder — one `<Route>`, no
  // `key` — so a filter left set from the last folder would silently apply
  // to this one, on an agency this folder may not even have a file from.
  // An effect would be one render late (the stale filter would flash against
  // the new folder's rows first), so this resets during render instead —
  // React's own pattern for "some state should reset when a prop changes"
  // (see "You Might Not Need an Effect").
  const [filterFolderId, setFilterFolderId] = useState(folderId);
  if (folderId !== filterFolderId) {
    setFilterFolderId(folderId);
    setAgencyFilter(undefined);
    setFilterOpen(false);
  }

  /*
    FIL-3a and FIL-3b, in that same panel and behind a third gate.

    `manageMode` is the panel's own state machine and not a second popover:
    `menu` is the two buttons, `rename` swaps the block for a name form, and
    `confirm` swaps it for the two-stage delete `RowActions` uses on a file. One
    panel means one blur timer and one Escape handler, which is the whole reason
    this is not a nested overflow.

    `manageName` seeds from the folder below, once it has loaded — this state is
    declared above the early returns for `subscription`'s reason.
  */
  const [manageMode, setManageMode] = useState<'menu' | 'rename' | 'confirm'>('menu');
  const [manageName, setManageName] = useState('');
  const [manageBusy, setManageBusy] = useState(false);
  const [manageError, setManageError] = useState<string | null>(null);

  /*
    FIL-3, moved here from `FolderDestinations`' cross-space picker — this
    panel already knows which space it is on, so there is nothing left to ask
    but a name. A state machine of its own rather than a fourth `manageMode`
    value: it is an act on the space, not on this folder, so it does not
    belong inside the block `folder?.canManage` gates.
  */
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderBusy, setNewFolderBusy] = useState(false);
  const [newFolderFailure, setNewFolderFailure] = useState<'none' | 'duplicate' | 'other'>('none');

  /**
   * The images prev/next moves between (FIL-8a).
   *
   * **Only viewable rows, and only ones with bytes.** Stepping onto a 6 GB MP4
   * would be a broken frame *and* an audited 6 GB download; stepping onto an
   * `Archive` or `Rehydrating` row would be a fetch for bytes that are not
   * online yet, which is the same reason `FileNameLink` renders those inert.
   *
   * **The total is honest because `useFiles` follows `continuationToken` to
   * exhaustion before it settles `loaded`** — its own header says so. If that
   * ever stops being true, "3 of 47" becomes a lie and this is where to look.
   */
  /**
   * The folder's files, in the order this screen presents them: by name.
   *
   * **Sorted here, above everything that reads them**, so the table, the card
   * grid, the summary line's count and the image viewer's "3 of 47" are all
   * describing one ordering. Sorting inside `FileTable` instead would leave the
   * viewer stepping through the server's order while the reader watched a
   * different one — and the arrow keys are the one place that divergence would
   * be silent.
   *
   * `compareSortValues` is `DataTable`'s own comparator, so the default sort it
   * is given below produces exactly this order rather than a near-miss of it.
   *
   * **A reader who clicks another header moves the table alone.** `DataTable`
   * owns that state internally; the cards and the viewer keep name order. Lifting
   * it out is a real refactor and not this change.
   */
  const files = useMemo(
    () =>
      state.kind === 'loaded'
        ? [...state.files].sort((a, b) => compareSortValues(a.name, b.name))
        : [],
    [state],
  );

  /**
   * CON-24's filter options — the agencies actually represented in this
   * folder, off the *unfiltered* `files` above and never off `visibleFiles`
   * below: computing this off the filtered list would collapse to one
   * option the moment a filter is applied, with no option left to read as
   * "back to All agencies."
   *
   * Off `uploadedBy.agencyName`, not the space roster's `agencyId` — a file
   * carries no `agencyId` (`fileSummarySchema`, `src/lib/api.ts`), only the
   * display-name string, and the roster's ids have nothing on a row to match.
   */
  const distinctAgencies = useMemo(
    () =>
      Array.from(new Set(files.map((file) => file.uploadedBy.agencyName))).sort(compareSortValues),
    [files],
  );

  /**
   * The filter applied, if any. `files` stays the whole, honestly-counted
   * folder — `FolderSummaryLine` below leans on that being true — so this is
   * a second list, read by everything that shows the *current view*: the
   * table, the cards, and (through `openable` next) the two lightbox lists.
   * `undefined` means "All agencies" and every row passes.
   */
  const visibleFiles = useMemo(
    () =>
      agencyFilter === undefined
        ? files
        : files.filter((file) => file.uploadedBy.agencyName === agencyFilter),
    [files, agencyFilter],
  );

  const openable = useMemo(
    () =>
      visibleFiles.filter(
        (file) => file.tierState !== 'Archive' && file.tierState !== 'Rehydrating',
      ),
    [visibleFiles],
  );

  const images = useMemo(() => openable.filter((file) => isViewableImage(file.name)), [openable]);

  /**
   * **A second list rather than a longer one**, and the separation is FIL-8b's.
   *
   * Photographs are a set a reviewer steps across, so `images` is what "3 of
   * 47" counts. A document is one thing you stay inside, and its footer counts
   * pages — so folding PDFs into `images` would make one footer mean two things
   * and start numbering a mixed set. Nothing steps between the two lists.
   */
  const documents = useMemo(() => openable.filter((file) => isViewablePdf(file.name)), [openable]);

  /**
   * Identity is the file's id and the index is derived from it, never the other
   * way round: if a `refresh()` drops the row mid-view, an index would quietly
   * show a different photograph, where a missing id closes the viewer.
   *
   * One search param serves both viewers, because `?view=` names a file rather
   * than a position — which is what makes a pasted space land on the right thing
   * without saying which kind of thing it is.
   */
  const viewing = searchParams.get('view');
  const viewIndex = images.findIndex((file) => file.id === viewing);
  const viewFile = viewIndex >= 0 ? images[viewIndex] : undefined;
  const viewDocument =
    viewFile === undefined ? documents.find((file) => file.id === viewing) : undefined;

  function openViewer(fileId: string) {
    const next = new URLSearchParams(searchParams);
    next.set('view', fileId);
    openedHere.current = true;
    setSearchParams(next);
  }

  function showViewerIndex(nextIndex: number) {
    const target = images[nextIndex];
    if (!target) return;

    const next = new URLSearchParams(searchParams);
    next.set('view', target.id);
    // Replace, never push: paging forty photographs must not leave forty
    // entries between the reader and the folder they came from.
    setSearchParams(next, { replace: true });
  }

  function closeViewer() {
    if (openedHere.current) {
      openedHere.current = false;
      // Back, so the entry this screen pushed is spent rather than stacked on.
      void navigate(-1);
      return;
    }

    // Arrived by a pasted space — there is no entry of ours to go back to, and
    // `navigate(-1)` would leave the app entirely.
    const next = new URLSearchParams(searchParams);
    next.delete('view');
    setSearchParams(next, { replace: true });
  }

  // Absent while the space is still loading, and absent for good if it failed.
  // Neither is an error here: this only decides the header and the panel.
  const loadedSpace = space.state.kind === 'loaded' ? space.state : undefined;
  const folder = loadedSpace?.folders.find((candidate) => candidate.id === folderId);
  const destination =
    destinations.state.kind === 'loaded'
      ? destinations.state.folders.find(
          (candidate) => candidate.spaceId === spaceId && candidate.folderId === folderId,
        )
      : undefined;
  /*
    CON-24, read off the roster this call already carries — the reader's own row
    in it. **False while the space is still loading and false for good if it
    failed**, on the same rule as `folder` above: this request is supplementary
    and must never gate the table, so its absence has to degrade to the generic
    wording rather than to an error or to a guess. The narrower string is the
    kinder one and it is not the safer one to assume.
  */
  const ownScope =
    loadedSpace?.space.members.find((member) => member.agencyId === viewerAgencyId)
      ?.visibilityScope === 'own';

  /*
    NTF-1's switch. Seeded from the folder card the space listing already
    carried, so it renders in its right position on first paint — and refreshed
    through `space.refresh` rather than by trusting local state, so the flag
    stays the server's answer.

    **Above the two early returns, and it has to be.** Placing it with the other
    header derivations below them made it a conditional hook: the file table's
    loading branch returns first, so the hook ran on the second render and not
    the first, and React tore the component down. The two `loadedSpace`
    derivations moved up with it — they are pure, so nothing else cares where
    they sit, and keeping them beside their one consumer is what stops this
    being re-introduced by somebody tidying them back down.

    **No `canWrite` gate, deliberately.** PERM-4b puts subscribing with reading:
    a read-only member may subscribe, and gating this on write would remove the control
    from exactly the readers a shared folder exists to serve.
  */
  const subscription = useFolderSubscription(
    spaceId,
    folderId,
    folder?.subscribed ?? false,
    FOLDER_COPY.notifications.failed,
    space.refresh,
  );

  if (state.kind === 'loading') {
    return (
      <p role="status" className="text-sm" style={{ color: token('bp-text-secondary') }}>
        {FOLDER_COPY.states.loading}
      </p>
    );
  }

  if (state.kind === 'error') {
    return <FolderError status={state.status} onRetry={retry} />;
  }

  const canWrite = loadedSpace?.space.canWrite ?? false;
  // From the space, not from the rows: an empty folder has no row to derive it
  // from, and the control would vanish exactly when the folder is empty enough
  // to want it (RET-7).
  const canSetRetention = loadedSpace?.space.canSetRetention ?? false;
  /**
   * **Whether retention is visible at all, distinct from `canSetRetention`.**
   * `canSetRetention` already implies a Docs-plan agency can never *change* a
   * class — the API derives it from `holdsEvidence` — but it says nothing
   * about *reading* one: retention is set by the uploading agency (PERM-6),
   * so a Docs-plan viewer can still be looking at a counterpart's Evidence-plan
   * upload that carries a class. `showRetention` hides the fact along with the
   * control, on the product's own read that retention is an Evidence-tier
   * concept end to end.
   */
  const showRetention = plan === 'DocsEvidence';

  /**
   * Whether the header offers CON-24's filter at all.
   *
   * `!ownScope`: an `own`-scope reader already sees only their own agency's
   * uploads, so a filter that could only ever offer their own name back
   * would be pointless. `distinctAgencies.length > 1`: a folder with one
   * uploading agency has nothing to filter down to either — the same rule
   * `canWrite` gating Upload and `RecycleBinButton` rendering nothing on an
   * empty bin already follow, against offering a control that cannot act.
   */
  const showAgencyFilter = !ownScope && distinctAgencies.length > 1;

  /*
    What a drag over the page should do (FIL-7, **ADR-0061**). Computed here
    because every fact that suppresses it is this component's own, and read
    nowhere else — `UploadPanel` forwards it without interpreting it.

    **`off` is not "stop listening".** It means the overlay does not paint and
    the files are discarded; the drop is still swallowed, because a drop that
    reaches the browser navigates the tab to `file:///…` and kills every
    transfer in flight.

    Two full-screen takeovers suppress it. `ViewerFrame` is opaque and covers
    the page, so an overlay under it is invisible and one over it is nonsense —
    the reader is inside a document, not looking at a folder — and silently
    uploading into the folder behind a document somebody is signing is a
    surprise with a permanent, audited consequence. `ConsentDialog` is a
    blocking decision, and a full-viewport scrim over a modal waiting for an
    answer is wrong on its face. The folder-controls popover is deliberately not
    on this list: a transient popover is not a takeover.

    **`loadedSpace === undefined` is `off` rather than `readOnly`.** The space
    call is supplementary and settles after the table, so `canWrite` is false in
    that window only because nobody has answered yet — and it stays false for
    good if the call failed. "Drop to add" and "read-only" would both be claims
    this screen cannot make, which is the same reasoning that leaves the header
    with neither the Upload button nor the read-only line here.
  */
  const dropMode: DropMode =
    viewFile !== undefined ||
    viewDocument !== undefined ||
    subscription.phase.kind === 'consenting' ||
    loadedSpace === undefined
      ? 'off'
      : canWrite
        ? 'accepting'
        : 'readOnly';

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-x-3 gap-y-1 lg:grid-cols-[minmax(0,1fr)_auto_auto_auto]">
        <div className="col-span-3 lg:col-span-4">
          <Eyebrow>{FOLDER_COPY.eyebrow}</Eyebrow>
        </div>
        {/*
          The title and the overflow trigger share a row at every width, the
          trigger pinned to the trailing edge; Upload rejoins that row only
          at `lg` — the same pairing `SpaceRoot`'s header gives its own name
          and New folder. Below `lg`, Upload falls to its own row instead,
          left-aligned, rather than competing with the trigger for the same
          right-hand edge on a narrow screen. `canWrite` gates the button
          exactly as it gated the old standing panel: visibility, not
          authorization (SEC-5, CON-13).

          **A `<label>`, not a `<button>`.** It targets `uploadInputId` —
          the file input `UploadPanel` still owns, rendered with
          `showChooser={false}` below — so clicking it opens the OS picker
          in one native step. No panel opens first: there is nothing here
          to toggle, which is the whole point of putting it in the header
          rather than leaving it as a standing dropzone.

          **The title sits outside the `folder === undefined` guard and the
          controls stay inside it.** `folder` comes from the supplementary
          `useFolders` call, which must never gate the file table — so
          "table loaded, space failed" is a designed state, and until M7's
          outline slice it rendered a working table under no heading at all,
          on the app's deepest route. The controls cannot follow the title
          out: `canWrite`/`canSetRetention` are false here only because the
          *space* failed, and a valid space returning a folder id its own
          list omits would otherwise render Upload on a page with no folder.
        */}
        {/*
          **The destination's name, for every role** (FIL-1e). An admin used to
          get the bare folder name here, because they had arrived through the
          space root and the room was the screen behind them. There is no screen
          behind them now, so the heading has to say which room this is — which
          is exactly what the destination list already computes.
        */}
        <h1
          className="row-start-2 col-start-1 text-xl font-semibold"
          style={{ color: token('bp-text') }}
        >
          {folder === undefined || loadedSpace === undefined
            ? FOLDER_COPY.states.unnamedFolder
            : (destination?.displayName ?? folder.name)}
        </h1>

        {/*
          The shape of the folder before the table: which agency this space is
          with, when it last moved, how many files, and how much they weigh.
          The first two come off the folder/space rows the supplementary call
          already returned, so they cost nothing and go absent together with
          the title's controls if that call failed — there is nothing truthful
          to render about an agency or a date we do not have.

          **The count and the total come from `state.files` — the loaded page,
          not the folder row.** `folder.fileCount` would have been the honest
          source once; it no longer needs to be, because `useFiles` now follows
          `continuationToken` to exhaustion before it ever calls the result
          `loaded` (`src/lib/useFiles.ts`), so `state.files` already **is** the
          whole folder by the time this renders. That is also what makes a byte
          total something this line can state at all: summing a page and
          presenting it as the folder's size is exactly the mistake this
          component used to warn readers away from computing.

          Not `MetaRow`: that component's `time` + `label` pair fits a card's
          two-fact metadata row, and this line has four, in an order the
          product asked for by name. The amber-for-time rule still applies to
          the one segment that is time — carried here the same way `MetaRow`
          carries it internally, a `<time>` coloured `bp-amber` — rather than
          bending a two-slot component to a four-part line it was not shaped
          for.
        */}
        <div className="row-start-3 col-start-1">
          {folder === undefined || loadedSpace === undefined ? null : (
            <FolderSummaryLine
              // Not the space's name: the heading above already carries it, for
              // every role, as the first half of `Space — Folder` (FIL-1d).
              // Repeating it here would say the room twice on one screen.
              lastActivityAt={folder.lastActivityAt}
              fileCount={files.length}
              totalBytes={files.reduce((total, file) => total + file.sizeBytes, 0)}
            />
          )}
        </div>

        {/*
          The filter trigger, the overflow trigger, and Upload no longer share
          a row. Each sits in its own cell beside the title at every width
          (`row-start-2`, `self-center` against the title's line height, one
          `auto` column apiece — filter then overflow, in that order, closest
          to the title outward); below `lg` that puts both beside the title
          on mobile too, right-aligned by the grid's own `auto` columns rather
          than needing a `flex` row to push them there. Upload gets `mt-4`
          below `lg`, where it is the fourth row of a `gap-y-1` grid —
          eyebrow, title, summary line, then a filled button 4px under a line
          of small grey text, which reads as attached to the sentence above it
          rather than as the folder's one act. At `lg` and up it rejoins the
          title's row, in the grid's fourth column, and needs no top margin.
        */}
        {folder === undefined ? null : (
          <>
            {showAgencyFilter ? (
              <span className="relative row-start-2 col-start-2 self-center">
                <button
                  type="button"
                  aria-haspopup="true"
                  aria-expanded={filterOpen}
                  aria-label={
                    agencyFilter === undefined
                      ? FOLDER_COPY.filter.trigger
                      : FOLDER_COPY.filter.activeTrigger(agencyFilter)
                  }
                  onClick={() => {
                    setFilterOpen((open) => !open);
                  }}
                  onBlur={() => {
                    // Same guard as the overflow trigger's `controlsPointerDown`
                    // below: a press landing inside the panel is a step in using
                    // it, not a reason to close it.
                    if (filterPointerDown.current) return;

                    filterBlurTimer.current = setTimeout(() => {
                      setFilterOpen(false);
                    }, 120);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') setFilterOpen(false);
                  }}
                  className="flex items-center justify-center rounded-md p-1.5 transition-colors hover:bg-elevated hover:text-body"
                  style={{
                    color: token(agencyFilter === undefined ? 'bp-text-secondary' : 'bp-blue-text'),
                  }}
                >
                  <FilterIcon />
                </button>

                {filterOpen ? (
                  <div
                    // `right-0`, same as the overflow trigger's below: this
                    // trigger sits one column left of it, close enough to the
                    // right edge on mobile that a `left-0` anchor grew the
                    // panel off the visible screen (`min-w-[12rem]` past a
                    // trigger already close to the edge). Anchoring off the
                    // trigger's own right edge instead keeps it on-screen at
                    // every width, and there is still room to its left for
                    // the title's wide `1fr` column to absorb it.
                    className="absolute top-full right-0 z-10 mt-1 min-w-[12rem] overflow-hidden rounded-md border p-3"
                    style={{
                      borderColor: token('bp-border'),
                      background: token('bp-panel'),
                      boxShadow: token('shadow-md'),
                    }}
                    onMouseDown={() => {
                      // Set the guard the trigger's `onBlur` reads (line ~704),
                      // not only clear the timer: the press that blurs the
                      // trigger re-arms the close timer *after* this fires, so
                      // clearing alone loses to a press held past the 120ms
                      // delay. This is the fix the overflow/controls panel
                      // already carries (see its block's `onMouseDown` below).
                      clearTimeout(filterBlurTimer.current);
                      filterPointerDown.current = true;
                    }}
                    onMouseUp={() => {
                      filterPointerDown.current = false;
                    }}
                  >
                    <Eyebrow>{FOLDER_COPY.filter.heading}</Eyebrow>

                    {/*
                      A plain button group, not the shared `Select` — the
                      overflow panel's retention picker below has the same
                      reason: `Select` portals its listbox to `document.body`,
                      and a click that opens it blurs this panel's own trigger
                      before the option can be reached.

                      Each pick closes the panel itself. It used to lean on the
                      trigger's re-armed blur timer to do that, but the
                      `filterPointerDown` guard above — needed so a deliberate
                      press is not cut short by that same timer — necessarily
                      suppresses the re-arm, so the close has to be explicit.
                      The blur timer still closes the panel on a press that
                      lands *outside* it.
                    */}
                    <div
                      role="group"
                      aria-label={FOLDER_COPY.filter.heading}
                      className="mt-1 flex flex-col"
                    >
                      <button
                        type="button"
                        aria-pressed={agencyFilter === undefined}
                        onClick={() => {
                          setAgencyFilter(undefined);
                          setFilterOpen(false);
                        }}
                        className="flex w-full items-center gap-2 px-1 py-1 text-left text-xs text-secondary transition-colors hover:bg-elevated hover:text-body"
                      >
                        <span className="flex size-3 shrink-0 items-center justify-center">
                          {agencyFilter === undefined ? <CheckIcon /> : null}
                        </span>
                        {FOLDER_COPY.filter.all}
                      </button>

                      {distinctAgencies.map((agency) => (
                        <button
                          key={agency}
                          type="button"
                          aria-pressed={agencyFilter === agency}
                          onClick={() => {
                            setAgencyFilter(agency);
                            setFilterOpen(false);
                          }}
                          className="flex w-full items-center gap-2 px-1 py-1 text-left text-xs text-secondary transition-colors hover:bg-elevated hover:text-body"
                        >
                          <span className="flex size-3 shrink-0 items-center justify-center">
                            {agencyFilter === agency ? <CheckIcon /> : null}
                          </span>
                          {agency}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
              </span>
            ) : null}

            {/*
              NTF-1 and RET-7's bulk picker, behind one trigger rather than
              standing beside Upload — `RowActions`' pattern: Upload is this
              header's one loud act, and a switch plus a searchable select
              next to it read as two more, each competing for the same
              weight. The switch has no `canWrite` gate, deliberately — a
              read-only member may subscribe (PERM-4b) — so the trigger always has
              at least that behind it.
            */}
            <span className="relative row-start-2 col-start-3 self-center">
              <button
                type="button"
                aria-haspopup="true"
                aria-expanded={controlsOpen}
                aria-label={FOLDER_COPY.folderControls.more}
                onClick={() => {
                  setControlsOpen((open) => !open);
                }}
                onBlur={() => {
                  // The press is inside the panel, so this blur is a step in
                  // using it rather than leaving it. See `controlsPointerDown`.
                  if (controlsPointerDown.current) return;

                  // Otherwise deferred, so a control that takes focus without a
                  // press still gets its activation in — `RowActions`' timing.
                  controlsBlurTimer.current = setTimeout(() => {
                    setControlsOpen(false);
                  }, 120);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') setControlsOpen(false);
                }}
                className="flex items-center justify-center rounded-md p-1.5 text-secondary transition-colors hover:bg-elevated hover:text-body"
              >
                <MoreActionsIcon />
              </button>

              {controlsOpen ? (
                <div
                  // `right-0`, unconditionally: the trigger sits beside the
                  // title, right-aligned in its own grid column, at every
                  // width now — not just at `lg` — so the panel always hangs
                  // its right edge off the trigger.
                  className="absolute top-full right-0 z-10 mt-1 min-w-[14rem] overflow-hidden rounded-md border p-3"
                  style={{
                    borderColor: token('bp-border'),
                    background: token('bp-panel'),
                    boxShadow: token('shadow-md'),
                  }}
                  // Beats the trigger's blur timer so the click below commits.
                  onMouseDown={() => {
                    clearTimeout(controlsBlurTimer.current);
                  }}
                >
                  {/*
                    Notifications remain a reader preference. Folder pinning
                    was retired by FIL-1d; every accessible folder is already
                    present in navigation.
                  */}
                  <Eyebrow>{FOLDER_COPY.folderControls.preferences}</Eyebrow>

                  <div className="mt-1 flex flex-col">
                    <PreferenceToggle
                      checked={subscription.subscribed}
                      onChange={subscription.toggle}
                      label={FOLDER_COPY.notifications.label}
                      busy={
                        subscription.phase.kind === 'saving' ||
                        subscription.phase.kind === 'checking'
                      }
                    />
                  </div>

                  {showRetention && canSetRetention ? (
                    // A button group, not the shared `Select` — that
                    // component's own listbox renders through a second
                    // portal, and a click that opens it blurs this panel's
                    // trigger before the option can be reached. `RetentionMenu`
                    // sidesteps the same nesting the same way, immediately
                    // below.
                    <div className="mt-3 border-t pt-3" style={{ borderColor: token('bp-border') }}>
                      <Eyebrow>{FOLDER_COPY.retention.bulk.label}</Eyebrow>
                      <div
                        role="group"
                        aria-label={FOLDER_COPY.retention.bulk.label}
                        className="mt-1"
                      >
                        {retentionClassSchema.options.map((retentionClass) => (
                          <button
                            key={retentionClass}
                            type="button"
                            disabled={bulkBusy}
                            onClick={() => void applyToFolder(retentionClass)}
                            className="flex w-full items-center px-1 py-1 text-left text-xs text-secondary transition-colors hover:bg-elevated hover:text-body disabled:opacity-60"
                          >
                            {FOLDER_COPY.retention.classes[retentionClass]}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {/*
                    **FIL-3, moved here from the cross-space picker
                    `FolderDestinations` used to hold.** The space is implicit
                    — this panel is already standing in one — so the act asks
                    for nothing but a name. `isAdmin && canWrite` mirrors that
                    picker's own visibility rule (an admin at a contributing
                    agency, CON-13); both clauses are visibility rather than
                    authorization, and the API refuses the write regardless
                    (SEC-5).
                  */}
                  {isAdmin && canWrite ? (
                    <div
                      className="mt-3 border-t pt-3"
                      style={{ borderColor: token('bp-border') }}
                      // `Manage`'s own reason: Cancel/Create are meant to let
                      // the panel's ordinary blur-close run after them, but
                      // opening the form is a second step inside the panel and
                      // needs the trigger's blur-close suppressed for the
                      // length of the press.
                      onMouseDown={() => {
                        clearTimeout(controlsBlurTimer.current);
                        controlsPointerDown.current = true;
                      }}
                      onMouseUp={() => {
                        controlsPointerDown.current = false;
                      }}
                    >
                      {newFolderOpen ? (
                        <form
                          onSubmit={(event) => {
                            event.preventDefault();
                            void submitNewFolder();
                          }}
                          className="flex flex-col gap-2"
                        >
                          <label
                            htmlFor="new-subfolder-name"
                            className="text-xs font-medium"
                            style={{ color: token('bp-text-secondary') }}
                          >
                            {FOLDER_COPY.newFolder.label}
                          </label>

                          <input
                            id="new-subfolder-name"
                            value={newFolderName}
                            onChange={(event) => {
                              setNewFolderName(event.target.value);
                            }}
                            maxLength={120}
                            autoComplete="off"
                            className="border px-3 py-2 text-sm"
                            style={{
                              background: token('bp-app'),
                              borderColor: token('bp-border-strong'),
                              color: token('bp-text'),
                              borderRadius: token('radius-md'),
                            }}
                          />

                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={closeNewFolder}
                              className={BUTTON_CLASS}
                              style={neutralButtonStyle()}
                            >
                              {FOLDER_COPY.newFolder.cancel}
                            </button>

                            <button
                              type="submit"
                              disabled={newFolderBusy || newFolderName.trim() === ''}
                              className={BUTTON_CLASS}
                              style={primaryButtonStyle()}
                            >
                              {newFolderBusy
                                ? FOLDER_COPY.newFolder.submitting
                                : FOLDER_COPY.newFolder.submit}
                            </button>
                          </div>

                          {newFolderFailure === 'none' ? null : (
                            <p role="alert" className="flex items-start gap-2 text-xs text-danger">
                              <StatusDot tone="alert" size="sm" />
                              {newFolderFailure === 'duplicate'
                                ? FOLDER_COPY.newFolder.duplicate
                                : FOLDER_COPY.newFolder.failed}
                            </p>
                          )}
                        </form>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setNewFolderOpen(true);
                          }}
                          className="flex w-full items-center gap-2 px-1 py-1 text-left text-xs text-secondary transition-colors hover:bg-elevated hover:text-body"
                        >
                          <PlusIcon />
                          {FOLDER_COPY.newFolder.open}
                        </button>
                      )}
                    </div>
                  ) : null}

                  {/*
                    FIL-3a and FIL-3b — the folder itself, behind a **third**
                    gate. `canManage` is neither `canWrite` nor
                    `canSetRetention`: a member may upload into a folder they
                    may not rename, and an admin of the space's *owner* agency
                    may not rename another member's folder at all (ADR-0074).
                    So it is its own block rather than a clause on one of the
                    two above.

                    Plain buttons, not the shared `Select` — the retention
                    block's reason, unchanged: a component that portals its own
                    listbox blurs this panel's trigger before an option can be
                    reached.
                  */}
                  {/*
                    `canManage` alone (FIL-1e). It already encodes the role —
                    the API sets it to `principal.role === 'admin' &&` the
                    creating-agency clause — so an `isAdmin` conjunction here
                    was a second, weaker copy of a rule the wire already
                    carries, and the space's root arrives with it false for
                    everybody.
                  */}
                  {folder?.canManage ? (
                    <div
                      className="mt-3 border-t pt-3"
                      style={{ borderColor: token('bp-border') }}
                      /*
                        **Scoped to this block, not the whole panel.** The
                        switch and the retention buttons are one-shot and are
                        meant to let the panel's ordinary 120ms auto-close run
                        after them — that used to be the whole reason the panel
                        went away once you flipped the switch, and an e2e test
                        depends on it (the panel reopens on the next click of
                        the trigger rather than double-toggling closed).
                        Rename and Delete open a *second step inside the
                        panel*, so only they need the trigger's blur-close
                        suppressed for the length of the press — which is why
                        this pair lives here and not on the panel's own
                        `onMouseDown` above.
                      */
                      onMouseDown={() => {
                        clearTimeout(controlsBlurTimer.current);
                        controlsPointerDown.current = true;
                      }}
                      onMouseUp={() => {
                        controlsPointerDown.current = false;
                      }}
                    >
                      <Eyebrow>{FOLDER_COPY.manage.label}</Eyebrow>

                      {manageMode === 'menu' ? (
                        <div className="mt-1 flex flex-col">
                          <button
                            type="button"
                            onClick={() => {
                              setManageName(folder.name);
                              setManageMode('rename');
                            }}
                            className="flex w-full items-center gap-2 px-1 py-1 text-left text-xs text-secondary transition-colors hover:bg-elevated hover:text-body"
                          >
                            <PencilIcon />
                            {FOLDER_COPY.manage.rename}
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              setManageMode('confirm');
                            }}
                            className="flex w-full items-center gap-2 px-1 py-1 text-left text-xs text-secondary transition-colors hover:bg-elevated hover:text-danger"
                          >
                            <TrashIcon />
                            {FOLDER_COPY.manage.delete}
                          </button>
                        </div>
                      ) : null}

                      {manageMode === 'rename' ? (
                        <form
                          onSubmit={(event) => {
                            event.preventDefault();
                            void submitRename();
                          }}
                          className="mt-2 flex flex-col gap-2"
                        >
                          <label
                            htmlFor="folder-rename"
                            className="text-xs font-medium"
                            style={{ color: token('bp-text-secondary') }}
                          >
                            {FOLDER_COPY.manage.nameLabel}
                          </label>

                          <input
                            id="folder-rename"
                            value={manageName}
                            onChange={(event) => {
                              setManageName(event.target.value);
                            }}
                            // The API's ceiling too (SEC-5).
                            maxLength={120}
                            autoComplete="off"
                            className="border px-3 py-2 text-sm"
                            style={{
                              background: token('bp-app'),
                              borderColor: token('bp-border-strong'),
                              color: token('bp-text'),
                              borderRadius: token('radius-md'),
                            }}
                          />

                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={closeControls}
                              className={BUTTON_CLASS}
                              style={neutralButtonStyle()}
                            >
                              {FOLDER_COPY.manage.cancel}
                            </button>

                            <button
                              type="submit"
                              disabled={manageBusy || manageName.trim() === ''}
                              className={BUTTON_CLASS}
                              style={primaryButtonStyle()}
                            >
                              {manageBusy ? FOLDER_COPY.manage.saving : FOLDER_COPY.manage.save}
                            </button>
                          </div>
                        </form>
                      ) : null}

                      {manageMode === 'confirm' ? (
                        <div className="mt-2 flex flex-col gap-2">
                          <p className="text-xs" style={{ color: token('bp-text-secondary') }}>
                            {FOLDER_COPY.manage.confirm}
                          </p>

                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={closeControls}
                              className={BUTTON_CLASS}
                              style={neutralButtonStyle()}
                            >
                              {FOLDER_COPY.manage.cancel}
                            </button>

                            <button
                              type="button"
                              disabled={manageBusy}
                              onClick={() => void submitDelete()}
                              className={BUTTON_CLASS}
                              // `RowActions`' confirm treatment: neutral
                              // outline, red label, so the destructive act does
                              // not out-weigh the one beside it.
                              style={{ ...neutralButtonStyle(), color: token('bp-danger') }}
                            >
                              {manageBusy
                                ? FOLDER_COPY.manage.deleting
                                : FOLDER_COPY.manage.confirmAction}
                            </button>
                          </div>
                        </div>
                      ) : null}

                      {manageError === null ? null : (
                        <p role="alert" className="mt-2 flex items-start gap-2 text-xs text-danger">
                          <StatusDot tone="alert" size="sm" />
                          {manageError}
                        </p>
                      )}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </span>

            {canWrite ? (
              <label
                htmlFor={uploadInputId}
                className={`${BUTTON_CLASS} row-start-4 col-start-1 mt-4 justify-self-start self-start lg:row-start-2 lg:col-start-4 lg:mt-0 lg:self-center`}
                style={primaryButtonStyle()}
              >
                {UPLOAD_COPY.heading}
              </label>
            ) : null}
          </>
        )}
      </div>

      {/*
        **Mounted whatever `canWrite` says**, which it was not before. The drop
        target lives inside it and has to answer a drag on a read-only folder
        too — and the stray-drop guard it absorbed has to run there, which it
        never did: a missed drop on a read-only folder navigated the tab away
        (ADR-0061). `canWrite` is a prop now rather than this branch.

        Nothing new paints for a read-only reader: the file input is gated on
        `canWrite` inside, the chooser is off, and the queue is empty.
      */}
      <UploadPanel
        spaceId={spaceId ?? ''}
        folderId={folderId ?? ''}
        canWrite={canWrite}
        drop={dropMode}
        folderName={folder?.name}
        showChooser={false}
        inputId={uploadInputId}
        // Refetch rather than push the row in: finalize answers with a bare
        // fileId, not the handle the download route takes, and the
        // counterpart may have added a file meanwhile.
        onUploaded={refresh}
      />

      {canWrite || loadedSpace === undefined ? null : (
        <p className="text-xs" style={{ color: token('bp-text-secondary') }}>
          {FOLDER_COPY.states.readOnly}
        </p>
      )}

      {bulkReport === undefined ? null : <BulkRetentionReport report={bulkReport} />}

      {downloadError === undefined ? null : (
        <p role="alert" className="flex items-center gap-2 text-sm text-danger">
          <StatusDot tone="alert" />
          {downloadError}
        </p>
      )}

      {/*
        A started retrieval is a `role="status"` and never an `alert`: nothing
        failed, and the answer is "come back in a few hours" (RET-9). It sits in
        its own slot rather than sharing `downloadError`'s, so the two cannot
        overwrite each other — a reader can plausibly hit a cap on one row and
        request a retrieval on another.
      */}
      {rehydrationNotice === undefined ? null : (
        <p role="status" className="flex items-center gap-2 text-sm">
          <StatusDot tone="blue" />
          <span style={{ color: token('bp-text-secondary') }}>{rehydrationNotice}</span>
        </p>
      )}

      {actionError === undefined ? null : (
        <p role="alert" className="flex items-center gap-2 text-sm text-danger">
          <StatusDot tone="alert" />
          {actionError}
        </p>
      )}

      {/*
        Its own slot rather than `actionError`'s, for the reason the rehydration
        notice has one: a reader can plausibly hit a delete failure on one row
        and a subscription failure on the switch, and one overwriting the other
        would lose whichever arrived first.
      */}
      {subscription.phase.kind === 'error' ? (
        <p role="alert" className="flex items-center gap-2 text-sm text-danger">
          <StatusDot tone="alert" />
          {subscription.phase.message}
        </p>
      ) : null}

      {/*
        NTF-4's one-time gate. Mounted here rather than beside the switch
        because it portals to `document.body` anyway — where it sits in this
        tree decides nothing about where it paints, only what it can read.
      */}
      <ConsentDialog
        open={subscription.phase.kind === 'consenting'}
        onAccept={subscription.accept}
        onCancel={subscription.dismiss}
        title={FOLDER_COPY.notifications.dialog.title}
        // The server's words, rendered exactly (NTF-4). Never a copy constant.
        statement={
          subscription.phase.kind === 'consenting' ? subscription.phase.clickwrap.text : ''
        }
        acceptLabel={FOLDER_COPY.notifications.dialog.accept}
        confirmLabel={FOLDER_COPY.notifications.dialog.confirm}
        cancelLabel={FOLDER_COPY.notifications.dialog.cancel}
      />

      {viewFile ? (
        <FileViewer
          file={viewFile}
          index={viewIndex}
          total={images.length}
          onShowIndex={showViewerIndex}
          onClose={closeViewer}
          onDownloadFailed={(message) => {
            setDownloadError(message);
          }}
          onDownloadStarted={() => {
            setDownloadError(undefined);
            setRehydrationNotice(undefined);
          }}
        />
      ) : null}

      {viewDocument ? (
        <DocumentViewer
          file={viewDocument}
          spaceId={spaceId ?? ''}
          folderId={folderId ?? ''}
          // FIL-8c/PERM-4a: signing produces an upload, so it needs what an
          // upload needs. Visibility only — the API refuses the session anyway.
          canSign={canWrite}
          onSigned={() => {
            // `useSigning.save` only reaches this on a successful upload — a
            // failed one leaves `error` set and the reader open so the person
            // can retry without re-placing anything.
            void refresh();
            closeViewer();
          }}
          onClose={closeViewer}
          onDownloadFailed={(message) => {
            setDownloadError(message);
          }}
          onDownloadStarted={() => {
            setDownloadError(undefined);
            setRehydrationNotice(undefined);
          }}
        />
      ) : null}

      <FileTable
        files={visibleFiles}
        mode={viewMode}
        ownScope={ownScope}
        agencyFilter={agencyFilter}
        showRetention={showRetention}
        // RET-12: the room's window drives the "expiring soon" hint; absent →
        // the 180-day default, which the hint helper reads as such.
        expiryWindowDays={loadedSpace?.space.expiryWindowDays}
        onView={openViewer}
        onDownloadFailed={(message) => {
          setDownloadError(message);
        }}
        onDownloadStarted={() => {
          setDownloadError(undefined);
          setRehydrationNotice(undefined);
        }}
        onRehydrated={(message) => {
          setRehydrationNotice(message);
          // The row has to stop offering retrieval and start saying it is under
          // way, and the server owns that state — so refetch, never patch.
          refresh();
        }}
        confirming={confirming}
        busyId={busyId}
        onAsk={(id) => {
          setActionError(undefined);
          setConfirming(id);
        }}
        onCancel={() => {
          setConfirming(undefined);
        }}
        onDelete={(id) => void remove(id)}
        onRetain={(id, retentionClass) => void retain(id, retentionClass)}
      />
    </div>
  );

  async function remove(id: string) {
    setBusyId(id);
    setActionError(undefined);

    try {
      await deleteFile(id);
      setConfirming(undefined);
      // Refetch rather than splice the row out: the server owns newest-first
      // ordering and it pages, and the counterpart may have uploaded meanwhile.
      refresh();
    } catch (caught) {
      // The API's own message, not a generic line. A 403 here names the rule —
      // only the uploading agency, only your own uploads — and a 409 says it is
      // already gone. Both are more use than "that did not work".
      setActionError(messageFrom(caught, FOLDER_COPY.delete.failed));
    } finally {
      setBusyId(undefined);
    }
  }

  async function retain(id: string, retentionClass: RetentionClass) {
    setBusyId(id);
    setActionError(undefined);

    try {
      await setFileRetention(id, retentionClass);
      setConfirming(undefined);
      // Refetch for `remove`'s reason, plus one of its own: the class *and* the
      // term come back from the server, and the term is computed there.
      refresh();
    } catch (caught) {
      // The API's message matters more here than anywhere on this screen. A 403
      // says the agency needs the Evidence add-on (RET-5) and a 409 names the
      // date before which Azure charges the full tier minimum regardless
      // (RET-8) — neither is anything a fallback line could reconstruct.
      setActionError(messageFrom(caught, FOLDER_COPY.retention.failed));
    } finally {
      setBusyId(undefined);
    }
  }

  /** Close the panel and put its manage block back to its two buttons. */
  function closeControls() {
    setControlsOpen(false);
    setManageMode('menu');
    setManageError(null);
  }

  async function submitRename() {
    setManageBusy(true);
    setManageError(null);

    try {
      await renameFolder(spaceId ?? '', folderId ?? '', manageName);
      closeControls();
      // The `<h1>` reads `folder.name`, which comes from the supplementary
      // space call — so the refetch is what updates the heading, not this
      // component's own state.
      space.refresh();
      folderDestinationsChanged();
    } catch (caught) {
      setManageError(messageFrom(caught, FOLDER_COPY.manage.renameFailed));
    } finally {
      setManageBusy(false);
    }
  }

  async function submitDelete() {
    setManageBusy(true);
    setManageError(null);

    try {
      await deleteFolder(spaceId ?? '', folderId ?? '');
      folderDestinationsChanged();
      // **Away, not refreshed.** This page's own address no longer resolves to
      // anything, and `replace` keeps a dead URL out of history so Back does
      // not land on a 404 the reader themselves created.
      void navigate(`/files/spaces/${encodeURIComponent(spaceId ?? '')}`, { replace: true });
    } catch (caught) {
      // The 409 lands here, and it is the ordinary refusal rather than a
      // failure: the caller may perform this act and the folder is not empty.
      setManageError(messageFrom(caught, FOLDER_COPY.manage.deleteFailed));
      setManageBusy(false);
    }
  }

  /** Close New subfolder and put it back to its own trigger. */
  function closeNewFolder() {
    setNewFolderOpen(false);
    setNewFolderName('');
    setNewFolderFailure('none');
  }

  async function submitNewFolder() {
    setNewFolderBusy(true);
    setNewFolderFailure('none');

    try {
      await createFolder(spaceId ?? '', newFolderName);
      closeNewFolder();
      // The new folder is a destination this list already has to know about —
      // the sidebar and `FolderDestinations` both read it — and neither is
      // this component's own state to patch.
      folderDestinationsChanged();
    } catch (caught) {
      // A 409 is the one failure with a specific fix — the name is taken, and
      // the typed value is kept so it can be changed rather than retyped —
      // `FolderDestinations`' own `CreateFolder` had the same rule.
      setNewFolderFailure(
        caught instanceof ApiError && caught.status === 409 ? 'duplicate' : 'other',
      );
    } finally {
      setNewFolderBusy(false);
    }
  }

  async function applyToFolder(retentionClass: RetentionClass) {
    setBulkBusy(true);
    setActionError(undefined);
    setBulkReport(undefined);

    try {
      // A 200 even when rows failed: the act is best-effort per file, so the
      // report is the response rather than an error (RET-7, A-7).
      setBulkReport(await setFolderRetention(spaceId ?? '', folderId ?? '', retentionClass));
      refresh();
    } catch (caught) {
      setActionError(messageFrom(caught, FOLDER_COPY.retention.bulk.failed));
    } finally {
      setBulkBusy(false);
    }
  }
}

/**
 * RET-7's folder-level convenience — **a convenience, and the code says so.**
 *
 * A-7 forbids a folder-level retention *attribute*: the case/matter concept §14
 * removed would be back through a side door. What keeps that structural rather
 * than aspirational is that the API loops over the per-file act and answers with
 * a per-file report, which is what this renders. Nothing here reads back a class
 * "on the folder", because there is none — which is also why the dropdown that
 * triggers it (in the header, above) is never a *standing* selection: it is
 * always `value=""`, on the invite drawer's precedent for "a
 * trigger, not a standing selection" — choosing an option applies it
 * immediately and the control resets, because there is no folder-level class
 * to keep showing.
 *
 * The report is a `role="status"` and never an `alert`. Rows succeeded; a
 * partial result is the designed outcome rather than a failure.
 */
function BulkRetentionReport({ report }: { report: FolderRetentionResult }) {
  return (
    <div
      role="status"
      className="flex flex-col gap-1 rounded-md border p-3 text-xs"
      style={{ borderColor: token('bp-border'), background: token('bp-panel') }}
    >
      <span style={{ color: token('bp-text') }}>
        {FOLDER_COPY.retention.bulk.applied(report.applied)}
      </span>

      {report.skipped === 0 ? null : (
        <span style={{ color: token('bp-text-secondary') }}>
          {FOLDER_COPY.retention.bulk.skipped(report.skipped)}
          {' · '}
          {FOLDER_COPY.retention.bulk.skippedWhy}
        </span>
      )}

      {report.failed.length === 0 ? null : (
        <>
          <span style={{ color: token('bp-danger') }}>
            {FOLDER_COPY.retention.bulk.failedHeading(report.failed.length)}
          </span>

          <ul className="flex flex-col gap-1">
            {report.failed.map((failure) => (
              <li key={failure.id} style={{ color: token('bp-text-secondary') }}>
                {failure.name}
                {': '}
                {/* The API's own sentence: a 409 names the date (RET-8). */}
                {failure.message}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/**
 * The refusals, each said in the terms the reader is in.
 *
 * A 404 is not "page not found": a folder in a space the caller's agency is not
 * on is absent by design (CON-11). A 403 is permanent, so it offers no retry.
 */
function FolderError({ status, onRetry }: { status: number; onRetry: () => void }) {
  const message =
    status === 403
      ? FOLDER_COPY.states.forbidden
      : status === 404
        ? FOLDER_COPY.states.missing
        : FOLDER_COPY.states.error;

  const permanent = status === 403 || status === 404;

  return (
    <div className="flex flex-col items-start gap-3">
      <p role="alert" className="flex items-center gap-2 text-sm text-danger">
        <StatusDot tone="alert" />
        {message}
      </p>

      {permanent ? null : (
        <button
          type="button"
          onClick={onRetry}
          className="w-fit border px-3 py-1.5 text-xs font-medium transition-colors"
          style={neutralButtonStyle()}
        >
          {FOLDER_COPY.states.retry}
        </button>
      )}
    </div>
  );
}

/**
 * `ImageViewer`, with this surface's download wired into it.
 *
 * A component rather than a branch in `FolderView` because `useFileDownload`
 * is a hook and the viewer is conditional — and because it keeps `ImageViewer`
 * knowing nothing about this file's hooks or copy. It takes the act and the
 * label, and a different surface could hand it different ones.
 */
function FileViewer({
  file,
  index,
  total,
  onShowIndex,
  onClose,
  onDownloadFailed,
  onDownloadStarted,
}: {
  file: FileSummary;
  index: number;
  total: number;
  onShowIndex: (index: number) => void;
  onClose: () => void;
  onDownloadFailed: (message: string) => void;
  onDownloadStarted: () => void;
}) {
  const { busy, download } = useFileDownload(file, onDownloadFailed, onDownloadStarted);

  return (
    <ImageViewer
      file={file}
      index={index}
      total={total}
      // Clamped, never wrapped: evidence review has a beginning and an end, and
      // wrapping would silently re-download — and re-audit — a file already
      // seen, while making "have I looked at them all" unanswerable.
      onStep={(delta) => {
        onShowIndex(Math.min(total - 1, Math.max(0, index + delta)));
      }}
      onJump={onShowIndex}
      onClose={onClose}
      onDownload={() => void download()}
      downloadLabel={busy ? FOLDER_COPY.download.working : FOLDER_COPY.download.label}
      downloadBusy={busy}
    />
  );
}

/**
 * `PdfViewer`, with this surface's download wired into it (FIL-8b).
 *
 * A sibling of `FileViewer` rather than a branch inside it, for the reason that
 * one exists at all: `useFileDownload` is a hook, so the choice of reader has to
 * be made by which component is mounted rather than by an expression inside
 * one. It takes no index and no total — a document is not a position in a set.
 */
function DocumentViewer({
  file,
  spaceId,
  folderId,
  canSign,
  onSigned,
  onClose,
  onDownloadFailed,
  onDownloadStarted,
}: {
  file: FileSummary;
  spaceId: string;
  folderId: string;
  canSign: boolean;
  onSigned: () => void;
  onClose: () => void;
  onDownloadFailed: (message: string) => void;
  onDownloadStarted: () => void;
}) {
  const { busy, download } = useFileDownload(file, onDownloadFailed, onDownloadStarted);

  return (
    <PdfViewer
      file={file}
      spaceId={spaceId}
      folderId={folderId}
      canSign={canSign}
      onSigned={onSigned}
      onClose={onClose}
      onDownload={() => void download()}
      downloadLabel={busy ? FOLDER_COPY.download.working : FOLDER_COPY.download.label}
      downloadBusy={busy}
    />
  );
}

/**
 * The panel's own on/off row — a checkbox rather than `Switch`'s pill,
 * **local to this file**, not a restyle of the shared component: `MembersTab`
 * and `EnrollmentLock` also render `<Switch>`, in contexts (an account's
 * status, an enrolment lock) that read correctly as a track and a knob and
 * are untouched here.
 *
 * **Sized to match `PencilIcon`/`TrashIcon` below, deliberately.** Pin,
 * Notify, Rename, and Delete are one column of four rows once this panel is
 * open, and a reader scanning down it should find every glyph starting at the
 * same x — a pill switch never could, at more than twice the width of a
 * 14px stroke icon.
 *
 * `role="switch"` and `aria-checked` survive the restyle unchanged — this is
 * still an immediate two-state control, not a pending choice a Save commits,
 * which is the whole reason `Switch` reaches for that role rather than
 * `aria-pressed` (see its own header). The accessible name still comes from
 * the button's own text, exactly as `Switch`'s did, so `findByRole('switch',
 * { name: … })` in `FolderViewPin.test.tsx` and `FolderViewNotifications.test.tsx`
 * needed no change.
 */
function PreferenceToggle({
  checked,
  onChange,
  label,
  busy,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  busy: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-busy={busy}
      disabled={busy}
      onClick={() => {
        // `Switch`'s own rule: the caller is told which state was asked for,
        // not that a press happened, so a request already in flight can
        // ignore a second press without remembering what the first one meant.
        if (!busy) onChange(!checked);
      }}
      className="flex w-full items-center gap-2 px-1 py-1 text-left text-xs text-secondary transition-colors hover:bg-elevated hover:text-body disabled:cursor-not-allowed disabled:opacity-60"
    >
      <ToggleBox checked={checked} />
      {label}
    </button>
  );
}

/**
 * `PreferenceToggle`'s glyph — an outlined box, empty off and holding a
 * checkmark on. **Never filled.** It used to fill `bp-blue` on and draw
 * `CheckIcon` in `currentColor`, inherited from the button's `text-secondary`
 * class rather than set explicitly — against the blue fill that read at
 * roughly 1.3:1 in the command room, well under WCAG's 3:1 floor for a
 * non-text control. The box now keeps the same bordered shape in both states
 * — `bp-text-faint` off, `bp-blue` on — and the checkmark's colour is set
 * explicitly to `bp-blue` rather than left to inherit from whatever text
 * happens to surround it.
 *
 * `size-4`, matching `PencilIcon`/`TrashIcon`'s own wrapper below so all four
 * rows' glyphs share one width regardless of which of them is a checkbox and
 * which is a plain icon.
 */
function ToggleBox({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden="true"
      className="flex size-4 shrink-0 items-center justify-center border"
      style={{
        borderColor: token(checked ? 'bp-blue' : 'bp-text-faint'),
        borderRadius: token('radius-sm'),
      }}
    >
      {checked ? (
        <span style={{ color: token('bp-blue') }}>
          <CheckIcon />
        </span>
      ) : null}
    </span>
  );
}
